import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavItem {
  label: string;
  path: string;
}

const navItems: NavItem[] = [
  { label: '首页', path: '/' },
  { label: '综艺', path: '/category/variety' },
  { label: '新闻', path: '/category/news' },
  { label: '趣味视频', path: '/category/videos' },
  { label: '搜索', path: '/search' },
  { label: '关于', path: '/about' },
];

interface NavbarProps {
  overlay?: boolean;
}

export default function Navbar({ overlay = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isSolid = !overlay || scrolled || menuOpen;

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
          : 'bg-gradient-to-b from-foreground-950/60 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="h-16 md:h-20 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0" aria-label="搞鸡娱乐资讯网首页">
            <span className="w-10 h-10 rounded-xl bg-primary-500 text-background-50 flex items-center justify-center text-xl font-bold">
              <i className="ri-fire-fill" />
            </span>
            <span className="flex flex-col leading-none">
              <span className={`font-logo text-2xl ${isSolid ? 'text-foreground-950' : 'text-background-50'}`}>
                搞鸡
              </span>
              <span className={`text-[10px] tracking-widest mt-1 ${isSolid ? 'text-foreground-500' : 'text-background-50/80'}`}>
                ENTERTAINMENT
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  isActive(item.path)
                    ? 'bg-primary-500 text-background-50'
                    : isSolid
                      ? 'text-foreground-700 hover:bg-primary-100 hover:text-primary-700'
                      : 'text-background-50/90 hover:bg-background-50/15'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Right action (desktop) */}
          <div className="hidden md:flex items-center gap-3 shrink-0">
            <Link
              to="/search"
              className="flex items-center gap-2 px-4 py-2 rounded-full border text-sm cursor-pointer whitespace-nowrap transition-colors bg-background-50 text-foreground-700 border-background-300 hover:border-primary-400"
            >
              <i className="ri-search-line text-base" />
              <span>搜你想看</span>
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="打开菜单"
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-xl cursor-pointer ${
              isSolid ? 'text-foreground-900 bg-background-100' : 'text-background-50'
            }`}
          >
            <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50">
          <nav className="px-4 py-3 flex flex-col gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium cursor-pointer ${
                  isActive(item.path)
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                <span>{item.label}</span>
                <i className="ri-arrow-right-s-line text-lg" />
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}